// Dev-only guided checklist for the meeting ↔ calendar manual smoke.
// State lives in sessionStorage so closing the dialog does not wipe the run.
// Production builds never include this module (@dev-only resolves to stubs).

import { reactive } from 'vue'

const STORAGE_KEY = 'cmv-cal-smoke-guide'

export const CAL_SMOKE_STEPS = [
  {
    id: 'baseline',
    title: 'Create and send',
    do: 'New meeting with a teacher and an admin. Only Save as draft and Save & notify should show. Save & notify.',
    checks: [
      {
        id: 'event',
        label: 'Event is on the case manager calendar with the right title, time, and guests'
      },
      { id: 'email', label: 'The test guest received the invitation email' }
    ]
  },
  {
    id: 'quiet-notes',
    title: 'Quiet notes edit',
    do: 'Edit the sent meeting. Change only the notes. Save without email (it should be the main button).',
    checks: [
      { id: 'desc', label: 'Calendar description updated' },
      { id: 'no-email', label: 'No email to any guest' },
      {
        id: 'rsvp',
        label:
          'Skip if you cannot see Yes/No on the Calendar event. The app does not show Google responses, and clicking Yes in the app does not change them. If you can see a response on the event, a notes-only save should leave it.'
      }
    ]
  },
  {
    id: 'buttons',
    title: 'Buttons follow the edit',
    do: 'Change only the time. Save & notify turns blue and Save without email stays clickable. Change the time back. Then add or remove a person who is not already on the team. Save without email turns off. Undo that person and it turns back on.',
    checks: [
      {
        id: 'time',
        label:
          'A time change makes Save & notify the main button. Save without email stays clickable.'
      },
      {
        id: 'team',
        label: 'A team change disables Save without email. Undoing that person turns it back on.'
      }
    ]
  },
  {
    id: 'quiet-time',
    title: 'Quiet time change',
    do: 'Change the time and choose Save without email.',
    checks: [
      { id: 'moved', label: 'The event moved on the guests’ calendars' },
      { id: 'no-email', label: 'No email was sent' }
    ]
  },
  {
    id: 'guest-notify',
    title: 'Guest changes must notify',
    do: 'Add a person (quiet save should be disabled) and Save & notify. Then remove a person and Save & notify.',
    checks: [
      { id: 'disabled', label: 'Save without email disabled as soon as the team changed' },
      {
        id: 'checkbox',
        label:
          'Save & notify opened the warning panel listing who is invited or removed, and its save button stayed off until “I understand” was checked'
      },
      {
        id: 'invite',
        label: 'Save & notify included the new person. Confirm the invitation email arrived.'
      },
      {
        id: 'cancel',
        label: 'Save & notify included the removed person. Confirm they got a cancellation.'
      }
    ]
  },
  {
    id: 'stale-time',
    title: 'Stale-time guard',
    do: 'Open Edit and leave it open. In Google Calendar, drag the event to a new time. Come back to this meeting, change only the notes, and click Save without email. A popup appears over the page and shows both times. OK keeps the Calendar time. Cancel keeps the time in this form and moves the event back. Then set the event to all-day in Calendar, close this dialog completely, and open it again. The meeting row should say all day.',
    checks: [
      { id: 'prompt', label: 'A popup showed the Calendar time and the time in this form' },
      { id: 'ok', label: 'OK on that popup kept the Calendar time and still saved the notes' },
      {
        id: 'cancel',
        label: 'Cancel on that popup kept the time in this form and moved the Calendar event back'
      },
      {
        id: 'all-day',
        label:
          'After closing and reopening, an all-day Calendar event shows as all day on the meeting row'
      }
    ]
  },
  {
    id: 'cal-add',
    title: 'Person added only in Calendar',
    do: 'In Calendar, add someone who is not already on this meeting and not the event owner. Wait until Calendar finishes saving. Close this dialog completely and open it again. The banner should name that person. Save & notify should stay off until you change the team. Save without email should still work and leave them on the event.',
    checks: [
      { id: 'chip', label: 'The row shows +N in Calendar and the editor banner names them' },
      { id: 'notes', label: 'A notes-only Save without email left them on the event' },
      {
        id: 'notify',
        label:
          'Save & notify stayed disabled, with a tooltip to match the lists before notifying. Save without email still worked and left the extra person.'
      },
      {
        id: 'warn',
        label:
          'A guest-change save warned they would be removed from the event. Cancel saved nothing.'
      },
      {
        id: 'match',
        label: 'Adding them and Save & notify cleared the chip and sent an invitation'
      }
    ]
  },
  {
    id: 'cal-remove',
    title: 'Person removed only in Calendar',
    do: 'In Calendar, remove a guest (not the room). Reopen this dialog. Save & notify without changing the team (it should warn and not put them back). Then change the team while they are still on it and continue. Then remove them from the team and save.',
    checks: [
      { id: 'chip', label: 'The row shows N not on Calendar and the banner names them' },
      {
        id: 'notify',
        label:
          'Save & notify stayed disabled until the team was changed. A notes-only save did not put them back.'
      },
      { id: 'warn', label: 'The confirm said they would be put back on the event' },
      { id: 'back', label: 'Continuing put them back on the event' },
      {
        id: 'accept',
        label: 'Removing them from the team made the lists match and cleared the chip'
      }
    ]
  },
  {
    id: 'swap',
    title: 'Swap in Calendar',
    do: 'In Calendar, remove one admin and add another. Reopen this dialog. In the app, remove the old one, add the new one, Save & notify.',
    checks: [
      { id: 'both', label: 'The chip says Calendar differs and the banner lists both people' },
      { id: 'match', label: 'After the save, the chip clears and guests were emailed' }
    ]
  },
  {
    id: 'room',
    title: 'Calendar-only room',
    do: 'In Calendar, add a room. Notes-only Save without email. Then change the team and Save & notify. Then type a location here and save.',
    checks: [
      { id: 'kept', label: 'The notes-only save left the Calendar room in place' },
      { id: 'dropped', label: 'The guest-change save dropped that room' },
      { id: 'replaced', label: 'A location saved here replaced the Calendar room' }
    ]
  },
  {
    id: 'owned',
    title: 'Meet, color, reminders',
    do: 'In Calendar, add a Meet link, change the color, and set a reminder. Then Save without email, then Save & notify.',
    checks: [
      { id: 'kept', label: 'Meet link, color, and reminder were still there after both saves' },
      { id: 'link', label: 'The editor has a link to change those in Google Calendar' }
    ]
  },
  {
    id: 'draft',
    title: 'Draft then send',
    do: 'New meeting, Save as draft. Then edit it and Save & notify.',
    checks: [
      { id: 'private', label: 'The draft event had no guests and sent no email' },
      { id: 'sent', label: 'Save & notify put the guests on the event and emailed invitations' }
    ]
  },
  {
    id: 'regress',
    title: 'Quick regressions',
    do: 'Delete a meeting. Use Remove former teachers if you have one. Check Gen-ed hours. Delete an event in Calendar and reopen this dialog.',
    checks: [
      { id: 'delete', label: 'Delete removed the event and emailed a cancellation' },
      { id: 'former', label: 'Remove former teachers updated the event with no email' },
      { id: 'hours', label: 'Gen-ed hours still count the meeting’s attendees' },
      {
        id: 'detached',
        label:
          'An event deleted in Calendar disappears from this list after you close and reopen. The meeting is kept as detached and is not shown here.'
      }
    ]
  }
]

const emptyState = () => ({
  startedAt: null,
  stepIndex: 0,
  checks: {},
  actions: []
})

export const meetingSmokeState = reactive(emptyState())

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meetingSmokeState))
  } catch {
    /* private mode / quota — the in-memory run still works */
  }
}

function hydrate() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw)
    meetingSmokeState.startedAt = saved.startedAt || null
    meetingSmokeState.stepIndex = Number(saved.stepIndex) || 0
    meetingSmokeState.checks = saved.checks && typeof saved.checks === 'object' ? saved.checks : {}
    meetingSmokeState.actions = Array.isArray(saved.actions) ? saved.actions : []
  } catch {
    /* ignore a corrupt blob */
  }
}

hydrate()

export function startMeetingSmoke() {
  if (meetingSmokeState.startedAt) return
  meetingSmokeState.startedAt = new Date().toISOString()
  persist()
}

// Records only during a run the tester started, so ordinary dev use of the
// dialog does not fill the log.
export function logMeetingSmoke(event, detail = {}) {
  if (!meetingSmokeState.startedAt) return
  const row = { t: new Date().toISOString(), event }
  for (const [key, value] of Object.entries(detail)) {
    if (value == null || value === '') continue
    row[key] = value
  }
  meetingSmokeState.actions.push(row)
  if (meetingSmokeState.actions.length > 200) {
    meetingSmokeState.actions.splice(0, meetingSmokeState.actions.length - 200)
  }
  applyAutoChecks()
  persist()
}

// A logged action can prove what the dialog did. It cannot see an inbox or
// the Google Calendar website, so those checks stay for you to mark.
function namedCount(value) {
  if (Array.isArray(value)) return value.filter(Boolean).length
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim()) return value.split(',').filter(Boolean).length
  return 0
}

function setAutoCheck(id, status, note) {
  const prev = meetingSmokeState.checks[id]
  if (prev && prev.auto === false) return
  meetingSmokeState.checks[id] = { status, note, auto: true }
}

// Pure so a unit test can feed a log without touching sessionStorage.
export function autoMarksFromActions(actions) {
  const rows = Array.isArray(actions) ? actions : []
  const marks = {}
  const set = (id, status, note) => {
    marks[id] = { status, note }
  }
  const has = (event, pred = () => true) => rows.some((row) => row.event === event && pred(row))

  if (
    has(
      'save-ok',
      (row) =>
        row.notify === 'none' &&
        namedCount(row.added) === 0 &&
        namedCount(row.removed) === 0 &&
        String(row.sentGuests) !== '1'
    )
  ) {
    set(
      'quiet-notes.no-email',
      'pass',
      'From the log: Save without email did not send the guest list.'
    )
  }
  if (has('schedule-dirty', (row) => String(row.on) === '1')) {
    set(
      'buttons.time',
      'pass',
      'From the log: the time changed, so Save & notify became the main button. Save without email stays available for a time-only change.'
    )
  }
  if (
    has('schedule-dirty', (row) => String(row.on) === '1') &&
    has('save-ok', (row) => row.notify === 'none')
  ) {
    set('quiet-time.no-email', 'pass', 'From the log: Save without email after a time change.')
  }
  if (has('guest-dirty', (row) => String(row.on) === '1')) {
    set(
      'guest-notify.disabled',
      'pass',
      'From the log: the team changed, so Save without email turned off.'
    )
  }
  const guestOn = rows.findIndex((row) => row.event === 'guest-dirty' && String(row.on) === '1')
  if (
    guestOn >= 0 &&
    rows.slice(guestOn + 1).some((row) => row.event === 'guest-dirty' && String(row.on) === '0')
  ) {
    set(
      'buttons.team',
      'pass',
      'From the log: a team change turned Save without email off, and undoing that person turned it back on.'
    )
  }
  if (has('quiet-blocked')) {
    set(
      'guest-notify.disabled',
      'pass',
      'From the log: Save without email was blocked because the team changed.'
    )
  }
  if (has('save-ok', (row) => row.notify === 'all' && namedCount(row.added) > 0)) {
    set(
      'guest-notify.invite',
      'pass',
      'From the log: Save & notify included someone new. Confirm the invitation email arrived.'
    )
  }
  if (has('save-ok', (row) => row.notify === 'all' && namedCount(row.removed) > 0)) {
    set(
      'guest-notify.cancel',
      'pass',
      'From the log: Save & notify included a removal. Confirm the cancellation email arrived.'
    )
  }
  if (has('guest-diff-choice')) {
    set(
      'guest-notify.checkbox',
      'pass',
      'From the log: the warning panel opened before the guest list was saved.'
    )
  }
  if (has('time-prompt')) {
    set('stale-time.prompt', 'pass', 'From the log: both times were shown.')
  }
  if (has('time-choice', (row) => String(row.keepCalendar) === '1')) {
    set('stale-time.ok', 'pass', 'From the log: you kept the Calendar time.')
  }
  if (has('time-choice', (row) => String(row.keepCalendar) === '0')) {
    set('stale-time.cancel', 'pass', 'From the log: you kept the time in the form.')
  }
  if (has('row-all-day')) {
    set(
      'stale-time.all-day',
      'pass',
      'From the log: a meeting row showed all day after the list loaded.'
    )
  }
  if (has('editor-open', (row) => Number(row.extra) > 0)) {
    set('cal-add.chip', 'pass', 'From the log: the editor opened with someone only on Calendar.')
  }
  if (
    has(
      'save-ok',
      (row) => row.notify === 'none' && String(row.sentGuests) !== '1' && Number(row.extra) > 0
    )
  ) {
    set(
      'cal-add.notes',
      'pass',
      'From the log: the quiet save did not send the guest list while someone was only on Calendar.'
    )
  }
  if (has('notify-blocked', (row) => namedCount(row.extra) > 0)) {
    set(
      'cal-add.notify',
      'pass',
      'From the log: Save & notify stayed off while someone was only on Calendar.'
    )
  }
  if (
    has('guest-diff-choice', (row) => namedCount(row.dropped) > 0 && String(row.continued) === '0')
  ) {
    set(
      'cal-add.warn',
      'pass',
      'From the log: the panel named who would leave the event, and you went back.'
    )
  }
  const addedOnly = rows.findIndex((row) => row.event === 'editor-open' && Number(row.extra) > 0)
  if (addedOnly >= 0) {
    const later = rows.slice(addedOnly + 1)
    const saved = later.findIndex(
      (row) => row.event === 'save-ok' && row.notify === 'all' && namedCount(row.added) > 0
    )
    if (
      saved >= 0 &&
      later.slice(saved + 1).some((row) => row.event === 'editor-open' && Number(row.extra) === 0)
    ) {
      set(
        'cal-add.match',
        'pass',
        'From the log: Save & notify followed someone only on Calendar, and the next open no longer showed them as extra. Confirm the invitation email arrived.'
      )
    }
  }
  if (has('editor-open', (row) => Number(row.missing) > 0)) {
    set(
      'cal-remove.chip',
      'pass',
      'From the log: the editor opened with someone on the meeting but not the event.'
    )
  }
  if (has('notify-blocked', (row) => namedCount(row.missing) > 0)) {
    set(
      'cal-remove.notify',
      'pass',
      'From the log: Save & notify stayed off while someone was missing from the event.'
    )
  }
  if (has('guest-diff-choice', (row) => namedCount(row.putBack) > 0)) {
    set(
      'cal-remove.warn',
      'pass',
      'From the log: the panel said they would be put back on the event.'
    )
  }
  if (
    has(
      'guest-diff-choice',
      (row) => namedCount(row.putBack) > 0 && String(row.continued) === '1'
    ) &&
    has('save-ok', (row) => row.notify === 'all')
  ) {
    set(
      'cal-remove.back',
      'pass',
      'From the log: you continued, and Save & notify wrote the meeting.'
    )
  }
  const missingOpen = rows.findIndex(
    (row) => row.event === 'editor-open' && Number(row.missing) > 0
  )
  if (missingOpen >= 0) {
    const later = rows.slice(missingOpen + 1)
    const saved = later.findIndex((row) => row.event === 'save-ok' && namedCount(row.removed) > 0)
    if (
      saved >= 0 &&
      later.slice(saved + 1).some((row) => row.event === 'editor-open' && Number(row.missing) === 0)
    ) {
      set(
        'cal-remove.accept',
        'pass',
        'From the log: they were removed from the team, and the next open showed no one missing from the event.'
      )
    }
  }
  if (has('editor-open', (row) => Number(row.extra) > 0 && Number(row.missing) > 0)) {
    set('swap.both', 'pass', 'From the log: both sides of the guest list differed.')
  }
  const swapOpen = rows.findIndex(
    (row) => row.event === 'editor-open' && Number(row.extra) > 0 && Number(row.missing) > 0
  )
  if (swapOpen >= 0) {
    const later = rows.slice(swapOpen + 1)
    const saved = later.findIndex(
      (row) =>
        row.event === 'save-ok' &&
        row.notify === 'all' &&
        namedCount(row.added) > 0 &&
        namedCount(row.removed) > 0
    )
    if (
      saved >= 0 &&
      later
        .slice(saved + 1)
        .some(
          (row) =>
            row.event === 'editor-open' && Number(row.extra) === 0 && Number(row.missing) === 0
        )
    ) {
      set(
        'swap.match',
        'pass',
        'From the log: Save & notify followed a two-sided difference, and the next open matched. Confirm guests were emailed.'
      )
    }
  }
  if (has('editor-open', (row) => String(row.draft) !== '1' && String(row.calLink) === '1')) {
    set(
      'owned.link',
      'pass',
      'From the log: the editor opened with a link to the Google Calendar event.'
    )
  }
  if (has('save-ok', (row) => String(row.draft) === '1')) {
    set('draft.private', 'pass', 'From the log: saved as a draft, so no invitations were sent.')
  }
  const draftAt = rows.findIndex((row) => row.event === 'save-ok' && String(row.draft) === '1')
  if (
    draftAt >= 0 &&
    rows
      .slice(draftAt + 1)
      .some((row) => row.event === 'save-ok' && String(row.draft) !== '1' && row.notify === 'all')
  ) {
    set(
      'draft.sent',
      'pass',
      'From the log: Save & notify followed a draft. Confirm the invitation email arrived.'
    )
  }
  if (has('delete-ok')) {
    set(
      'regress.delete',
      'pass',
      'From the log: the meeting was deleted. Confirm the cancellation email yourself.'
    )
  }
  if (has('former-ok')) {
    set(
      'regress.former',
      'pass',
      'From the log: former teachers were removed and the save did not email.'
    )
  }
  if (has('reconcile', (row) => Number(row.cancelled) > 0)) {
    set(
      'regress.detached',
      'pass',
      'From the log: Google reported the event gone and the list refreshed. The meeting stays stored as detached and is hidden here.'
    )
  }

  rows.forEach((row, index) => {
    const guestChange =
      row.event === 'save-ok' &&
      row.notify === 'all' &&
      (namedCount(row.added) > 0 || namedCount(row.removed) > 0)
    if (!guestChange) return
    const warned = rows.slice(0, index).some((earlier) => earlier.event === 'guest-diff-choice')
    if (!warned) {
      set(
        'guest-notify.checkbox',
        'fail',
        'From the log: a guest change saved without the warning panel.'
      )
    }
  })
  return marks
}

function applyAutoChecks() {
  const marks = autoMarksFromActions(meetingSmokeState.actions)
  for (const [id, mark] of Object.entries(marks)) {
    setAutoCheck(id, mark.status, mark.note)
  }
}

export function setSmokeCheck(id, status, note) {
  startMeetingSmoke()
  const prev = meetingSmokeState.checks[id] || {}
  meetingSmokeState.checks[id] = {
    status: status == null ? prev.status || '' : status,
    note: note != null ? note : prev.note || '',
    auto: false
  }
  persist()
}

export function setSmokeStep(index) {
  const max = CAL_SMOKE_STEPS.length - 1
  meetingSmokeState.stepIndex = Math.min(max, Math.max(0, index))
  persist()
}

export function resetMeetingSmoke() {
  const fresh = emptyState()
  meetingSmokeState.startedAt = fresh.startedAt
  meetingSmokeState.stepIndex = 0
  meetingSmokeState.checks = {}
  meetingSmokeState.actions = []
  persist()
}

function allChecks() {
  const rows = []
  for (const step of CAL_SMOKE_STEPS) {
    for (const check of step.checks) {
      const saved = meetingSmokeState.checks[`${step.id}.${check.id}`] || {}
      rows.push({
        step: step.title,
        label: check.label,
        status: saved.status || 'unmarked',
        note: saved.note || ''
      })
    }
  }
  return rows
}

export function meetingSmokeReport({ studentId = '', studentLabel = '' } = {}) {
  const checks = allChecks()
  const counts = { pass: 0, fail: 0, skip: 0, unmarked: 0 }
  for (const row of checks) counts[row.status] = (counts[row.status] || 0) + 1
  return {
    kind: 'meeting-calendar-manual-smoke',
    startedAt: meetingSmokeState.startedAt,
    copiedAt: new Date().toISOString(),
    studentId,
    studentLabel,
    counts,
    checks,
    actions: meetingSmokeState.actions.slice()
  }
}

export function meetingSmokeMarkdown(meta = {}) {
  const report = meetingSmokeReport(meta)
  const lines = [
    'Meeting calendar smoke',
    `student: ${report.studentLabel || '—'} (${report.studentId || '—'})`,
    `started: ${report.startedAt || '—'}`,
    `pass ${report.counts.pass} · fail ${report.counts.fail} · skip ${report.counts.skip} · unmarked ${report.counts.unmarked}`,
    ''
  ]
  let lastStep = ''
  for (const row of report.checks) {
    if (row.step !== lastStep) {
      lines.push(row.step)
      lastStep = row.step
    }
    const mark = row.status === 'pass' ? 'pass' : row.status === 'fail' ? 'FAIL' : row.status
    lines.push(`- ${mark}: ${row.label}${row.note ? ` — ${row.note}` : ''}`)
  }
  lines.push('', 'Actions')
  if (!report.actions.length) lines.push('(none yet)')
  for (const action of report.actions) {
    const extra = Object.entries(action)
      .filter(([key]) => key !== 't' && key !== 'event')
      .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(', ') : value}`)
      .join(' ')
    lines.push(`${action.t.slice(11, 19)} ${action.event}${extra ? ` ${extra}` : ''}`)
  }
  return lines.join('\n')
}
